/**
 * Download All Images — bulk image downloader
 *
 * Exposes three static HTML entry points — `data/inject/core/index.html`,
 * `data/ui/index.html` and `data/gallery/index.html` — to every origin with no
 * `use_dynamic_url`, so each is fetchable from any page at a fixed URL.
 *
 * There are no images in the WAR group, so the primary signal fetches the most
 * distinctive HTML path. Serving any of these confirms the extension is installed.
 *
 * Manifest verified against version 1.0.9.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'nnffbdeachhbpfapjklmpnmjcgamcdmm';

export const downloadAllImages: ExtensionDefinition = {
  id: ID,
  name: 'Download All Images',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'Exposes three static HTML entry points to every origin with no dynamic URL. ' +
    'Detection is a single fetch and conclusive.',
  signals: [
    {
      id: 'war-inject-core-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch data/inject/core/index.html — the injected core UI page exposed to every ' +
        'origin at a fixed path.',
      weight: 1,
      run: () => probeFetch(ID, 'data/inject/core/index.html'),
    },
    {
      id: 'war-ui-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch data/ui/index.html — a second entry point, corroborating the ' +
        'identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'data/ui/index.html'),
    },
    {
      id: 'war-gallery-html',
      kind: 'web-accessible-resource',
      description: 'Fetch data/gallery/index.html — a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'data/gallery/index.html'),
    },
  ],
};
